import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Download,
  Filter,
  Loader2,
  Search,
  Trash2,
  Users,
  CalendarDays,
  TrendingUp,
  Mail,
  Phone,
  Briefcase,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { fetchLeads, fetchLeadStats, deleteLead } from "@/lib/api";

const PROJECTS = ["All", "Silva City", "Commercial Spaces", "Premium NA Plotting", "Other"];
const SOURCES = [
  { label: "All Sources", value: "all" },
  { label: "Contact Form", value: "contact_form" },
  { label: "Brochure Download", value: "brochure_download" },
];

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const StatCard = ({ icon: Icon, label, value, accent = "gold" }) => (
  <div className="glass-card p-6 card-lift">
    <div className="flex items-center justify-between">
      <p className="text-[10px] tracking-[0.28em] uppercase text-white/55">{label}</p>
      <span
        className={`flex h-8 w-8 items-center justify-center rounded-full border ${
          accent === "emerald"
            ? "border-[color:rgba(4,99,7,0.45)] bg-[color:var(--sp-emerald-soft)]"
            : "border-[color:rgba(212,175,55,0.45)] bg-[color:var(--sp-gold-soft)]"
        }`}
      >
        <Icon
          className={`h-4 w-4 ${
            accent === "emerald" ? "text-[color:var(--sp-emerald)]" : "text-[color:var(--sp-gold)]"
          }`}
        />
      </span>
    </div>
    <p className="font-display text-3xl md:text-4xl mt-3 text-white [font-variant-numeric:tabular-nums]">{value}</p>
  </div>
);

const AdminDashboardPage = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState("All");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (project && project !== "All") filters.project = project;
      if (sourceFilter && sourceFilter !== "all") filters.source = sourceFilter;
      const [list, s] = await Promise.all([fetchLeads(filters), fetchLeadStats()]);
      setLeads(list);
      setStats(s);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Failed to load data. Please retry.");
    } finally {
      setLoading(false);
    }
  }, [project, sourceFilter, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return leads;
    const q = search.trim().toLowerCase();
    return leads.filter((l) =>
      [l.fullName, l.emailAddress, l.phoneNumber, l.projectOfInterest, l.message]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [search, leads]);

  const onDelete = async (id) => {
    try {
      setDeleting(id);
      await deleteLead(id);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Lead deleted.");
      // refresh stats lightly
      const s = await fetchLeadStats();
      setStats(s);
    } catch {
      toast.error("Failed to delete lead.");
    } finally {
      setDeleting(null);
    }
  };

  const onExport = () => {
    if (!filtered.length) {
      toast.info("No leads to export.");
      return;
    }
    const headers = [
      "Date",
      "Full Name",
      "Email",
      "Phone",
      "Project of Interest",
      "Source",
      "Message",
    ];
    const rows = filtered.map((l) => [
      l.createdAt,
      l.fullName,
      l.emailAddress,
      l.phoneNumber,
      l.projectOfInterest,
      l.source,
      (l.message || "").replace(/\n/g, " "),
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `seetai-leads-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Leads exported.");
  };

  return (
    <AdminLayout
      eyebrow="Dashboard"
      title="Welcome back, Admin."
      description="Real-time view of all leads captured through the Seetai Properties website."
      actions={
        <button
          onClick={onExport}
          data-testid="admin-leads-export-button"
          className="btn-ghost inline-flex items-center gap-2 px-5 py-3 text-[11px] tracking-[0.22em] uppercase"
        >
          <Download className="h-4 w-4" /> Export CSV
        </button>
      }
    >
      {/* Stats */}
      <div data-testid="admin-stats" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        <StatCard icon={Users} label="Total Leads" value={stats?.total ?? "—"} />
        <StatCard icon={CalendarDays} label="Last 7 Days" value={stats?.last_7_days ?? "—"} />
        <StatCard icon={TrendingUp} label="Last 30 Days" value={stats?.last_30_days ?? "—"} accent="emerald" />
        <StatCard
          icon={Briefcase}
          label="Silva City Interest"
          value={stats?.by_project?.["Silva City"] ?? "—"}
        />
      </div>

      {/* Filters */}
      <div className="glass-card p-5 md:p-6 flex flex-col lg:flex-row gap-4 lg:items-center mb-6 min-w-0">
        <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-white/65 shrink-0">
            <Filter className="h-3.5 w-3.5 text-[color:var(--sp-gold)]" /> Filters
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-1 min-w-0">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/45" />
              <input
                data-testid="admin-search-input"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input-luxe w-full pl-10 pr-4 py-2.5 text-sm"
                placeholder="Search by name, email, phone, or message…"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:w-auto sm:min-w-[420px]">
              <div className="min-w-0">
                <Select value={project} onValueChange={setProject}>
                  <SelectTrigger data-testid="admin-filter-project" className="input-luxe w-full px-4 py-2.5 text-sm h-auto">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border border-white/12 text-white">
                    {PROJECTS.map((p) => (
                      <SelectItem key={p} value={p} className="focus:bg-white/8 focus:text-[color:var(--sp-gold)]">
                        {p === "All" ? "All Projects" : p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-0">
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger data-testid="admin-filter-source" className="input-luxe w-full px-4 py-2.5 text-sm h-auto">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border border-white/12 text-white">
                    {SOURCES.map((s) => (
                      <SelectItem key={s.value} value={s.value} className="focus:bg-white/8 focus:text-[color:var(--sp-gold)]">
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Leads Table */}
        <div data-testid="admin-leads-table" className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table data-testid="admin-leads-table-element" className="w-full text-sm">
              <thead className="bg-white/[0.03] border-b border-white/10 text-white/55 text-[10px] tracking-[0.24em] uppercase">
                <tr>
                  <th className="text-left px-5 py-4 font-medium">Date</th>
                  <th className="text-left px-5 py-4 font-medium">Name</th>
                  <th className="text-left px-5 py-4 font-medium">Contact</th>
                  <th className="text-left px-5 py-4 font-medium">Project</th>
                  <th className="text-left px-5 py-4 font-medium">Source</th>
                  <th className="text-left px-5 py-4 font-medium">Message</th>
                  <th className="text-right px-5 py-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center text-white/55">
                      <Loader2 className="h-5 w-5 inline-block mr-2 animate-spin text-[color:var(--sp-gold)]" /> Loading leads…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center text-white/55">
                      <div className="flex flex-col items-center gap-3">
                        <Users className="h-6 w-6 text-[color:var(--sp-gold)]" />
                        <p className="font-display text-xl text-white">No leads found.</p>
                        <p className="text-xs text-white/45">Submissions from the public site will appear here in real time.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((l) => (
                    <tr
                      key={l.id}
                      data-testid={`admin-lead-row-${l.id}`}
                      className="border-b border-white/8 hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="px-5 py-4 align-top whitespace-nowrap text-white/70">{formatDate(l.createdAt)}</td>
                      <td className="px-5 py-4 align-top">
                        <p className="text-white font-medium">{l.fullName}</p>
                      </td>
                      <td className="px-5 py-4 align-top text-white/80 space-y-1">
                        <p className="flex items-center gap-2">
                          <Mail className="h-3.5 w-3.5 text-white/45" />
                          <a href={`mailto:${l.emailAddress}`} className="hover:text-[color:var(--sp-gold)]">
                            {l.emailAddress}
                          </a>
                        </p>
                        <p className="flex items-center gap-2">
                          <Phone className="h-3.5 w-3.5 text-white/45" />
                          <a href={`tel:${l.phoneNumber}`} className="hover:text-[color:var(--sp-gold)]">
                            {l.phoneNumber}
                          </a>
                        </p>
                      </td>
                      <td className="px-5 py-4 align-top">
                        <span className="inline-flex items-center rounded-full border border-[color:rgba(212,175,55,0.35)] bg-[color:var(--sp-gold-soft)] px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase text-[color:var(--sp-gold)]">
                          {l.projectOfInterest}
                        </span>
                      </td>
                      <td className="px-5 py-4 align-top">
                        <span
                          className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase ${
                            l.source === "brochure_download"
                              ? "border-[color:rgba(4,99,7,0.45)] bg-[color:var(--sp-emerald-soft)] text-[color:var(--sp-emerald)]"
                              : "border-white/15 bg-white/4 text-white/80"
                          }`}
                        >
                          {l.source === "brochure_download" ? "Brochure" : "Contact"}
                        </span>
                      </td>
                      <td className="px-5 py-4 align-top text-white/65 max-w-[260px]">
                        {l.message ? (
                          <p className="line-clamp-2">{l.message}</p>
                        ) : (
                          <span className="text-white/30">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4 align-top text-right">
                        <div className="flex items-center justify-end gap-2">
                          <a
                            href={`https://wa.me/${(l.phoneNumber || "").replace(/\D/g, "")}`}
                            target="_blank"
                            rel="nofollow noopener noreferrer"
                            className="inline-flex items-center gap-1 rounded-md border border-white/12 px-2.5 py-1.5 text-[10px] tracking-[0.18em] uppercase text-white/70 hover:text-[color:var(--sp-gold)] hover:border-[color:rgba(212,175,55,0.4)] transition-colors"
                            title="Open in WhatsApp"
                          >
                            <ExternalLink className="h-3 w-3" /> WA
                          </a>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button
                                data-testid={`admin-leads-delete-button-${l.id}`}
                                className="inline-flex items-center gap-1 rounded-md border border-red-500/30 bg-red-500/10 px-2.5 py-1.5 text-[10px] tracking-[0.18em] uppercase text-red-300 hover:bg-red-500/15 transition-colors"
                                disabled={deleting === l.id}
                              >
                                {deleting === l.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Trash2 className="h-3 w-3" />
                                )}
                                Delete
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-[#121212] border border-white/12 text-white">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="font-display text-2xl text-white">Delete this lead?</AlertDialogTitle>
                                <AlertDialogDescription className="text-white/65">
                                  This will permanently remove <span className="text-[color:var(--sp-gold)]">{l.fullName}</span>’s
                                  enquiry from the database. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-white/5 border-white/15 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  data-testid={`admin-confirm-delete-${l.id}`}
                                  onClick={() => onDelete(l.id)}
                                  className="bg-red-500/90 text-white hover:bg-red-500"
                                >
                                  Delete Lead
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="mt-6 text-xs text-white/40">
          Showing {filtered.length} of {leads.length} leads {project !== "All" ? ` · ${project}` : ""}
        </p>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
